import { FunctionComponent, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
  useTheme
} from "@mui/material";
import { Group, InfoOutlined, OpenInNew } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { officeChartAccentColor } from "../Dashboard2/utils/dashboard2-status-display.utils";
import { ManagementDisplayUnitToggle } from "./components/ManagementDisplayUnitToggle";
import { ManagementDonutChart } from "./components/ManagementDonutChart";
import { ManagementChartEmptyState } from "./components/ManagementChartEmptyState";
import { ManagementFilterSelect } from "./components/ManagementFilterSelect";
import { ManagementLoadErrorAlert } from "./components/ManagementLoadErrorAlert";
import { ManagementPeriodNavigation } from "./components/ManagementPeriodNavigation";
import {
  ManagementSectionCard,
  managementSectionCardProminentTitleSx
} from "./components/ManagementSectionCard";
import { ManagementStatusPeriodTooltip } from "./components/ManagementStatusPeriodTooltip";
import { ManagementPrintButton } from "./components/ManagementPrintButton";
import { useRemoteFetchManagementDashboard } from "./hooks/useRemoteFetchManagementDashboard";
import { useManagementPrint } from "./hooks/useManagementPrint";
import type { ManagementDashboardStatusIdApi } from "./typings/management-dashboard.types";
import type { ManagementDisplayUnit } from "./typings/management-display-unit";
import {
  buildManagementDashboardQueryParams,
  mapManagementDashboardResponse,
  type ManagementStatusLabelKey
} from "./utils/map-management-dashboard-response";
import {
  formatLocationRankingValue,
  formatManagementChartTooltipValue,
  formatStatusBreakdownValue,
  getManagementPeriodChartYAxisProps,
  getOccupancyDonutDisplay,
  getOccupancyPeriodDataKey,
  getStatusDonutDisplay,
  getStatusPeriodDataKeys
} from "./utils/management-display-unit.utils";
import {
  getManagementChartTooltipProps,
  getManagementNeutralChartFill
} from "./utils/management-chart-theme.utils";
import {
  formatManagementPeriodLabel,
  ManagementPeriodMode
} from "./utils/management-period-picker.utils";
import { getManagementLocationDetailPath } from "./utils/management-location-detail-path.utils";
import {
  formatManagementFilterSelectValue,
  getSingleLocationIdForDrill
} from "./utils/management-filter-select.utils";
import { managementTouchIconButtonSx } from "./utils/management-touch-target.styles";
import { formatManagementLocationDisplayName } from "./utils/management-capa-chart.utils";

const STATUS_DONUT_LABEL_KEYS: Record<ManagementDashboardStatusIdApi, ManagementStatusLabelKey> = {
  office: "_management_statusOffice",
  mobileWork: "_management_statusMobile",
  notSpecified: "_management_statusNotSpecified"
};

/**
 * Management landing: occupancy + HR status overview.
 */
const ManagementPage: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const bookingsOnSaturdaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSaturdaysAllowed
  );
  const bookingsOnSundaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSundaysAllowed
  );

  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [periodMode, setPeriodMode] = useState<ManagementPeriodMode>("week");
  const [periodAnchor, setPeriodAnchor] = useState(() => DateTime.now().startOf("week"));
  const [displayUnit, setDisplayUnit] = useState<ManagementDisplayUnit>("percent");

  const queryParams = useMemo(
    () =>
      buildManagementDashboardQueryParams({
        periodMode,
        periodAnchor,
        locationIds,
        departmentIds
      }),
    [departmentIds, locationIds, periodAnchor, periodMode]
  );

  const { data, isLoading, isError, isFetching, refetch } =
    useRemoteFetchManagementDashboard(queryParams);
  const showInitialLoading = isLoading && !data;
  const showRefetchProgress = isFetching && Boolean(data);
  const { contentRef, handlePrint } = useManagementPrint(t("Management"));
  const dashboard = useMemo(
    () =>
      data
        ? mapManagementDashboardResponse(
            data,
            periodMode,
            {
              bookingsOnSaturdaysAllowed,
              bookingsOnSundaysAllowed
            },
            i18n.language
          )
        : null,
    [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed, data, i18n.language, periodMode]
  );

  const checkedInRatio =
    !dashboard || dashboard.plannedOfficeCount === 0
      ? 0
      : Math.round((dashboard.checkedInCount / dashboard.plannedOfficeCount) * 100);

  const periodLabel = useMemo(
    () => formatManagementPeriodLabel(periodMode, periodAnchor, i18n.language),
    [i18n.language, periodAnchor, periodMode]
  );

  const occupancyChartTitle =
    periodMode === "week" ? t("_management_occupancyWeek") : t("_management_occupancyMonth");
  const statusesChartTitle =
    periodMode === "week" ? t("_management_statusesWeek") : t("_management_statusesMonth");
  const chartTickInterval = periodMode === "month" ? 4 : 0;

  const shiftPeriod = (direction: -1 | 1) => {
    setPeriodAnchor(prev =>
      periodMode === "week" ? prev.plus({ weeks: direction }) : prev.plus({ months: direction })
    );
  };

  const handlePeriodSelect = (mode: ManagementPeriodMode, anchor: DateTime) => {
    setPeriodMode(mode);
    setPeriodAnchor(anchor);
  };

  const insight = dashboard?.insights[0];
  const occupancyDonut = dashboard ? getOccupancyDonutDisplay(dashboard, displayUnit) : null;
  const statusDonut = dashboard ? getStatusDonutDisplay(dashboard, displayUnit) : null;
  const occupancyPeriodDataKey = getOccupancyPeriodDataKey(displayUnit);
  const statusPeriodDataKeys = getStatusPeriodDataKeys(displayUnit);
  const showPercentUnit = displayUnit === "percent";
  const periodChartYAxis = getManagementPeriodChartYAxisProps(displayUnit);
  const chartTooltipProps = getManagementChartTooltipProps(theme);
  const chartAxisTick = { fontSize: 11, fill: theme.palette.text.secondary };
  const statusDonutColors: Record<ManagementDashboardStatusIdApi, string> = {
    office: officeChartAccentColor(theme),
    mobileWork: theme.palette.info.main,
    notSpecified: getManagementNeutralChartFill(theme)
  };

  const locationOptions = useMemo(
    () =>
      (dashboard?.locations ?? []).map(loc => ({
        id: loc.id,
        name: formatManagementLocationDisplayName(loc.name, t)
      })),
    [dashboard?.locations, t]
  );
  const departmentOptions = useMemo(
    () => (dashboard?.departments ?? []).map(dept => ({ id: dept.id, name: dept.name })),
    [dashboard?.departments]
  );

  const selectedLocationName = formatManagementFilterSelectValue(
    locationIds,
    locationOptions,
    t("All locations")
  );
  const selectedDepartmentName = formatManagementFilterSelectValue(
    departmentIds,
    departmentOptions,
    t("All departments")
  );
  const chartScopeLabel = [selectedLocationName, selectedDepartmentName, periodLabel].join(" · ");
  const locationDrillPath = getManagementLocationDetailPath(
    getSingleLocationIdForDrill(locationIds)
  );

  return (
    <Box
      ref={contentRef}
      data-testid="management-page"
      sx={{
        width: "100%"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, minWidth: 0 }}>
          {t("Management")}
        </Typography>
        <ManagementPrintButton onPrint={handlePrint} />
      </Stack>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2, gap: 2 }}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ minWidth: 0 }}
          data-testid="management-overview-subtitle"
        >
          {t("_management_overviewSubtitle")}
        </Typography>
        <Box sx={{ alignSelf: { xs: "flex-end", sm: "auto" } }}>
          <ManagementDisplayUnitToggle value={displayUnit} onChange={setDisplayUnit} />
        </Box>
      </Stack>

      {showInitialLoading && (
        <Box
          data-testid="management-loading"
          sx={{ display: "flex", justifyContent: "center", py: 6 }}
        >
          <CircularProgress aria-label={t("_management_loading")} />
        </Box>
      )}

      {isError && !showInitialLoading && (
        <ManagementLoadErrorAlert
          messageKey="_management_loadError"
          onRetry={() => {
            refetch().catch(() => undefined);
          }}
          testId="management-error"
        />
      )}

      {dashboard && (
        <>
          {showRefetchProgress && (
            <LinearProgress
              data-testid="management-refetch-progress"
              aria-label={t("_management_loading")}
              sx={{ mb: 1, borderRadius: 1 }}
            />
          )}
          {insight && (
            <Box
              data-testid="management-insight"
              sx={{
                mb: 3,
                px: 2,
                py: 1.5,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "action.hover",
                color: "text.primary"
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t(insight.templateKey, insight.params)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t(`${insight.templateKey}_interpretation`, insight.params)}
              </Typography>
            </Box>
          )}

          <Grid2 container spacing={2} sx={{ mb: 2 }}>
            <Grid2 size={{ xs: 12, sm: 5, md: 3 }}>
              <ManagementSectionCard minHeight={280}>
                <Stack spacing={0} sx={{ height: "100%", minHeight: 180 }}>
                  <Typography
                    component="h2"
                    data-testid="management-company-name"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.5rem", md: "1.625rem" },
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      textWrap: "balance",
                      color: "text.primary",
                      pb: 1
                    }}
                  >
                    {dashboard.companyName}
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 3.5, gap: 1.5 }} data-testid="management-filters">
                    <ManagementFilterSelect
                      multiple
                      label={t("Location")}
                      labelId="management-location-label"
                      options={locationOptions}
                      allLabel={t("All locations")}
                      value={locationIds}
                      onChange={setLocationIds}
                      data-testid="management-location-select"
                    />
                    <ManagementFilterSelect
                      multiple
                      label={t("Department")}
                      labelId="management-department-label"
                      options={departmentOptions}
                      allLabel={t("All departments")}
                      value={departmentIds}
                      onChange={setDepartmentIds}
                      data-testid="management-department-select"
                    />
                  </Stack>
                  <Box sx={{ mt: 3 }} data-testid="management-total-users-container">
                    <Box data-testid="management-total-users" sx={{ mb: 3 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        display="block"
                        sx={{ fontWeight: 600, mb: 0.25 }}
                      >
                        {t("Users")}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Group fontSize="small" color="action" aria-hidden />
                        <Typography
                          sx={{
                            fontFamily: "Fira Code, monospace",
                            fontWeight: 700,
                            fontSize: "1.25rem",
                            lineHeight: 1.2,
                            color: "text.primary"
                          }}
                        >
                          {dashboard.totalUsers}
                        </Typography>
                      </Stack>
                    </Box>

                    <ManagementPeriodNavigation
                      periodMode={periodMode}
                      periodAnchor={periodAnchor}
                      periodLabel={periodLabel}
                      onPrevious={() => shiftPeriod(-1)}
                      onNext={() => shiftPeriod(1)}
                      onSelect={handlePeriodSelect}
                    />
                  </Box>{" "}
                </Stack>
              </ManagementSectionCard>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 7, md: 9 }}>
              <ManagementSectionCard
                title={t("_management_todayOverview")}
                titleTestId="management-today-title"
                titleSx={managementSectionCardProminentTitleSx}
                minHeight={280}
              >
                <Box
                  data-testid="management-today-panel"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                    gap: { xs: 2, md: 0 },
                    alignItems: "stretch",
                    flex: 1,
                    minHeight: 0
                  }}
                >
                  <Box sx={{ px: { md: 1 }, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600 }}>
                        {t("_management_occupancyToday")}
                      </Typography>
                      <IconButton
                        component={RouterLink}
                        to={locationDrillPath}
                        aria-label={t("_management_openLocationDetail")}
                        data-testid="management-section-drill-today-location"
                        sx={managementTouchIconButtonSx}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Stack>
                    {occupancyDonut && (
                      <ManagementDonutChart
                        percentOrValue={occupancyDonut.percentOrValue}
                        remainder={occupancyDonut.remainder}
                        centerLabel={occupancyDonut.centerLabel}
                        centerSublabel={t("Occupied")}
                        activeSegmentLabel={t("Occupied")}
                        remainderSegmentLabel={t("Free")}
                        valueSuffix={occupancyDonut.valueSuffix}
                        size="large"
                        testId="management-occupancy-donut"
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      textAlign="center"
                    >
                      {showPercentUnit
                        ? t("_management_occupiedFreePercent", {
                            occupied: dashboard.occupancyTodayPercent,
                            free: Math.max(0, 100 - dashboard.occupancyTodayPercent)
                          })
                        : t("_management_occupiedFree", {
                            occupied: dashboard.occupiedSeats,
                            free: dashboard.freeSeats
                          })}
                    </Typography>
                    {dashboard.locationRanking.length > 0 && (
                      <Stack
                        spacing={0.5}
                        sx={{ mt: 1.5 }}
                        data-testid="management-location-ranking"
                      >
                        {dashboard.locationRanking.map(row => (
                          <Box
                            key={row.id}
                            component={RouterLink}
                            to={getManagementLocationDetailPath(row.id)}
                            data-testid="management-location-ranking-row"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 1,
                              textDecoration: "none",
                              color: "inherit",
                              borderRadius: 1,
                              px: 0.5,
                              "&:hover": {
                                bgcolor: "action.hover"
                              },
                              "&:focus-visible": {
                                bgcolor: "action.hover",
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: 2
                              }
                            }}
                          >
                            <Typography variant="caption">{row.name}</Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: "Fira Code, monospace", fontWeight: 600 }}
                            >
                              {formatLocationRankingValue(row, displayUnit)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: "none", md: "block" }, mx: 2 }}
                  />
                  <Divider sx={{ display: { xs: "block", md: "none" } }} />

                  <Box sx={{ px: { md: 1 }, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600 }}>
                        {t("_management_todaysStatus")}
                      </Typography>
                      <IconButton
                        component={RouterLink}
                        to="/management/users"
                        aria-label={t("_management_openUserStatusDetail")}
                        data-testid="management-section-drill-today-users"
                        sx={managementTouchIconButtonSx}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Stack>
                    {statusDonut && (
                      <ManagementDonutChart
                        centerLabel={statusDonut.centerLabel}
                        centerSublabel={t("_management_plannedOffice")}
                        valueSuffix={statusDonut.valueSuffix}
                        size="large"
                        testId="management-status-donut"
                        segments={statusDonut.segments.map(segment => ({
                          name: t(STATUS_DONUT_LABEL_KEYS[segment.id]),
                          value: segment.value,
                          color: statusDonutColors[segment.id]
                        }))}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      textAlign="center"
                      data-testid="management-planned-vs-checked-in"
                    >
                      {t("_management_plannedVsCheckedIn", {
                        checkedIn: dashboard.checkedInCount,
                        planned: dashboard.plannedOfficeCount,
                        percent: checkedInRatio
                      })}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1.5 }} data-testid="management-status-breakdown">
                      {dashboard.statusBreakdown.map(row => {
                        const isNotSpecified = row.id === "notSpecified";
                        return (
                          <Stack
                            key={row.id}
                            direction="row"
                            alignItems="center"
                            spacing={0.25}
                            sx={{ minWidth: 0 }}
                          >
                            <Box
                              component={RouterLink}
                              to="/management/users"
                              data-testid="management-status-breakdown-row"
                              sx={{
                                display: "flex",
                                flex: 1,
                                minWidth: 0,
                                justifyContent: "space-between",
                                gap: 1,
                                textDecoration: "none",
                                color: "inherit",
                                borderRadius: 1,
                                px: 0.5,
                                "&:hover": {
                                  bgcolor: "action.hover"
                                },
                                "&:focus-visible": {
                                  bgcolor: "action.hover",
                                  outline: "2px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: 2
                                }
                              }}
                            >
                              <Typography variant="caption">{t(row.labelKey)}</Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontFamily: "Fira Code, monospace", fontWeight: 600 }}
                              >
                                {formatStatusBreakdownValue(row, displayUnit)}
                              </Typography>
                            </Box>
                            {isNotSpecified ? (
                              <MuiTooltip
                                title={
                                  <Typography>{t("_management_statusNotSpecifiedHelp")}</Typography>
                                }
                              >
                                <IconButton
                                  size="small"
                                  aria-label={t("_management_statusNotSpecifiedHelp")}
                                  data-testid="management-status-not-specified-help"
                                  sx={managementTouchIconButtonSx}
                                >
                                  <InfoOutlined fontSize="small" />
                                </IconButton>
                              </MuiTooltip>
                            ) : null}
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              </ManagementSectionCard>
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <ManagementSectionCard
                title={occupancyChartTitle}
                subtitle={chartScopeLabel}
                drillTo={locationDrillPath}
                drillAriaLabel={t("_management_openLocationDetail")}
                drillTestId="management-section-drill-occupancy"
                minHeight={320}
              >
                <Box
                  sx={{ width: "100%", height: 240 }}
                  data-testid="management-occupancy-chart"
                  data-period-day-count={dashboard.occupancyPeriodDays.length}
                >
                  {dashboard.occupancyPeriodDays.length === 0 ? (
                    <ManagementChartEmptyState />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dashboard.occupancyPeriodDays}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis
                          dataKey="dateLabel"
                          tick={chartAxisTick}
                          interval={chartTickInterval}
                        />
                        <YAxis
                          domain={periodChartYAxis.domain}
                          allowDecimals={periodChartYAxis.allowDecimals}
                          tick={chartAxisTick}
                          tickFormatter={periodChartYAxis.tickFormatter}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            formatManagementChartTooltipValue(value, displayUnit)
                          }
                          contentStyle={chartTooltipProps.contentStyle}
                          itemStyle={chartTooltipProps.itemStyle}
                          labelStyle={chartTooltipProps.labelStyle}
                        />
                        <Line
                          type="monotone"
                          dataKey={occupancyPeriodDataKey}
                          name={t("Occupancy")}
                          stroke={theme.palette.primary.main}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </ManagementSectionCard>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <ManagementSectionCard
                title={statusesChartTitle}
                subtitle={chartScopeLabel}
                drillTo="/management/users"
                drillAriaLabel={t("_management_openUserStatusDetail")}
                drillTestId="management-section-drill-statuses"
                minHeight={320}
              >
                <Box
                  sx={{ width: "100%", height: 240 }}
                  data-testid="management-status-bar-chart"
                  data-period-day-count={dashboard.statusesPeriodDays.length}
                >
                  {dashboard.statusesPeriodDays.length === 0 ? (
                    <ManagementChartEmptyState />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboard.statusesPeriodDays}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis
                          dataKey="dateLabel"
                          tick={chartAxisTick}
                          interval={chartTickInterval}
                        />
                        <YAxis
                          domain={periodChartYAxis.domain}
                          allowDecimals={periodChartYAxis.allowDecimals}
                          tick={chartAxisTick}
                          tickFormatter={periodChartYAxis.tickFormatter}
                        />
                        <Tooltip
                          content={
                            <ManagementStatusPeriodTooltip
                              displayUnit={displayUnit}
                              notSpecifiedDataKey={statusPeriodDataKeys.notSpecified}
                            />
                          }
                        />
                        <Legend
                          wrapperStyle={{ color: theme.palette.text.primary }}
                          formatter={(value, entry) => {
                            const isNotSpecified = value === t("_management_statusNotSpecified");
                            const color = isNotSpecified
                              ? theme.palette.text.secondary
                              : entry.color ?? theme.palette.text.primary;
                            return <span style={{ color }}>{value}</span>;
                          }}
                        />
                        <Bar
                          dataKey={statusPeriodDataKeys.office}
                          name={t("_management_statusOffice")}
                          fill={statusDonutColors.office}
                        />
                        <Bar
                          dataKey={statusPeriodDataKeys.mobileWork}
                          name={t("_management_statusMobile")}
                          fill={statusDonutColors.mobileWork}
                        />
                        <Bar
                          dataKey={statusPeriodDataKeys.notSpecified}
                          name={t("_management_statusNotSpecified")}
                          fill={statusDonutColors.notSpecified}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </ManagementSectionCard>
            </Grid2>
          </Grid2>
        </>
      )}
    </Box>
  );
};

export default ManagementPage;
